import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FusionCharts from "fusioncharts";
import FusionMaps from "fusioncharts/fusioncharts.maps";
import FusionTheme from "fusioncharts/themes/fusioncharts.theme.fusion";
import USA from "fusionmaps/maps/fusioncharts.usa";
import React from "react";
import ReactFC from "react-fusioncharts";
import { KeyValue } from "../../interfaces";
import HeatBoxIcon from "./HeatBoxIcon";
import { licenseKey } from "./license";

const PREFIX = "USAMap";

const classes = {
  cardDisplay: `${PREFIX}-cardDisplay`,
  statesHeading: `${PREFIX}-statesHeading`,
  territory: `${PREFIX}-territory`,
};

const Root = styled("div")<{
  $isCountyView: boolean;
  dontShowCountyData: boolean;
}>(({ theme, $isCountyView, dontShowCountyData }) => ({
  [`& .${classes.cardDisplay}`]: {
    "border": `1px solid ${theme.palette.primary.main}`,
    "borderRadius": 8,
    "color": theme.palette.primary.main,
    "& .MuiTypography-body1": {
      fontSize: ".875rem",
      lineHeight: "1rem",
      paddingBottom: ".5rem",
    },
    "& .MuiCardContent-root": {
      padding: "1rem .5rem .5rem .5rem",
    },
  },

  [`& .${classes.statesHeading}`]: {
    position: "relative",
    color: theme.palette.primary.main,
    fontSize: "1rem",
    fontWeight: "bolder",
    paddingLeft: 0,
    textAlign: "left",
    paddingBottom: 7,
  },

  [`& .${classes.territory}`]: {
    "& .MuiTypography-body2": {
      fontSize: ".875rem",
      whiteSpace: "nowrap",
      paddingTop: 2,
      fontWeight: 400,
      paddingBottom: 0,
      cursor: "default",
    },
  },
  "& .fusioncharts-container svg g path": {
    cursor: !dontShowCountyData ? "pointer" : "default",
  },
  "& .fusioncharts-container svg g[cursor='pointer'][fill='#b1b2b7']": {
    display: "none",
  },
  "& .fusioncharts-container svg [class^='raphael-group-'] rect": {
    cursor: $isCountyView ? "default" : "pointer",
    ["cursor" as any]: `${$isCountyView ? "default" : "pointer"} !important`,
  },
  "& .heat-box-icon .fusioncharts-container svg [class^='raphael-group-'] rect":
    {
      ["cursor" as any]: "default !important",
    },
}));

interface MapData {
  id: string;
  value: number;
}

interface Props {
  mapData: MapData[];
  usaTotal: number;
  eventType: string;
}

(FusionCharts as any).options["license"]({
  key: licenseKey,
  creditLabel: false,
});

ReactFC.fcRoot(FusionCharts, FusionMaps, USA, FusionTheme);

export const handleMouseOver = (
  label: string,
  shortLabel: string,
  stateElement: KeyValue,
  usaTotalsElement: KeyValue,
  totalsByState: KeyValue,
) => {
  if (stateElement && stateElement.current) {
    stateElement.current.innerText = label;
  }

  if (usaTotalsElement && usaTotalsElement.current && shortLabel) {
    usaTotalsElement.current.innerText = totalsByState[shortLabel]
      ? `${totalsByState[shortLabel].toLocaleString()}`
      : "0";
  }
  return {
    state: stateElement,
    branches: usaTotalsElement,
  };
};

export const handleMouseOut = (
  stateElement: KeyValue,
  usaTotal: number,
  usaTotalsElement: KeyValue,
) => {
  if (stateElement && stateElement.current) {
    stateElement.current.innerText = "All States and Territories";
  }
  if (usaTotalsElement && usaTotalsElement.current) {
    usaTotalsElement.current.innerText = `${usaTotal.toLocaleString()}`;
  }

  return {
    state: stateElement,
    branches: usaTotalsElement,
  };
};

export default function USAMap({ mapData, usaTotal, eventType }: Props) {
  const stateElement = React.useRef<HTMLInputElement>(
    document.createElement("state") as HTMLInputElement,
  );
  const usaTotalsElement = React.useRef<HTMLInputElement>(null);
  const totalsByState: KeyValue = mapData.reduce((a, c) => {
    return { ...a, [c.id]: c.value };
  }, {});

  if (usaTotalsElement?.current) {
    usaTotalsElement.current.innerText = `${usaTotal.toLocaleString()}`;
  }

  const [legendRangeMinValue, setLegendRangeMinValue] = React.useState(0);
  const [legendRangeMaxValue, setLegendRangeMaxValue] = React.useState(10000);
  const max = Math.max(...mapData.map((d) => d.value));
  const stateMidMax = {
    low: 0,
    mid: Math.floor(max / 2),
    max: max,
  };

  const handleRangeUpdated = (eventObj: KeyValue) => {
    setLegendRangeMinValue(eventObj.data.minValue);
    setLegendRangeMaxValue(eventObj.data.maxValue);
  };
  const usStatesAndTerritories = [
    { abbr: "AS", label: "American Samoa" },
    {
      abbr: "FM",
      label: "Federated States of Micronesia",
      altLabel: "F.S. of Micronesia",
    },
    { abbr: "DC", label: "District of Columbia" },
    { abbr: "DE", label: "Delaware" },
    { abbr: "GU", label: "Guam" },
    { abbr: "MH", label: "Marshall Islands" },
    { abbr: "MP", label: "Northern Mariana Islands" },
    { abbr: "PW", label: "Palau" },
    { abbr: "PR", label: "Puerto Rico" },
    { abbr: "RI", label: "Rhode Island" },
    { abbr: "VI", label: "US Virgin Islands" },
  ];
  const mapType = "maps/usa";

  const [chartId, setChartId] = React.useState(`usa-map-${Date.now()}`);

  React.useEffect(() => {
    return () => {
      // Clean up the chart instance when the component unmounts
      if (FusionCharts(chartId)) {
        FusionCharts(chartId).dispose();
      }
    };
  }, [chartId]);

  const colorRangeUSA = {
    minvalue: stateMidMax.low,
    startlabel: "Low",
    endlabel: "High",
    code: "#FFFFA2",
    gradient: "1",
    color: [
      {
        maxvalue: stateMidMax.mid,
        code: "#ED4630",
        displayValue: "Middle",
      },
      {
        maxvalue: stateMidMax.max,
        code: "#B2D1FD",
        displayValue: "High",
      },
    ],
  };

  const mapChartProps = {
    type: mapType,
    width: "750",
    height: "550",
    dataFormat: "JSON",
    dataSource: {
      chart: {
        animation: "0",
        caption: "Rollover / click a state for more information",
        entityfillhovercolor: "#EEEEEE",
        showlabels: "1",
        borderthickness: "0.4",
        theme: "fusion",
        showLegend: "1",
        basefontcolor: "#013a5d",
        showToolTip: "1",
        entitytooltext: `<div style='padding:2px 4px;'><b>$lname</b><br>$value ${eventType}</div>`,
        toolTipBgColor: "#FFFFFF",
        toolTipColor: "#013a5d",
        toolTipBorderColor: "#013a5d",
        toolTipBorderThickness: "1",
        toolTipPadding: "8",
        showToolTipShadow: "1",
        numberScaleValue: "1000,1000,1000",
        numberScaleUnit: "K,M,B",
      },
      colorrange: colorRangeUSA,
      data: mapData,
    },
    events: {
      entityRollover: (event: KeyValue, data: KeyValue) => {
        handleMouseOver(
          data.label,
          data.shortLabel,
          stateElement,
          usaTotalsElement,
          totalsByState,
        );
      },
      entityRollout: () => {
        handleMouseOut(stateElement, usaTotal, usaTotalsElement);
      },
      legendRangeUpdated: (eventObj: KeyValue) => handleRangeUpdated(eventObj),
    },
  };

  const showStatesAndTerritories = (minValue: number, maxValue: number) => {
    return (
      <>
        {usStatesAndTerritories.map((territory: KeyValue, index: number) => {
          return (
            <Box
              key={index}
              onMouseOver={() => {
                handleMouseOver(
                  territory.altLabel ? territory.altLabel : territory.label,
                  territory.abbr,
                  stateElement,
                  usaTotalsElement,
                  totalsByState,
                );
              }}
              onMouseOut={() =>
                handleMouseOut(stateElement, usaTotal, usaTotalsElement)
              }
            >
              <Grid container wrap="nowrap">
                <Grid>
                  <HeatBoxIcon
                    amount={totalsByState[territory.abbr]}
                    rangeMinValue={minValue}
                    rangeMaxValue={maxValue}
                  />
                </Grid>
                <Grid className={classes.territory}>
                  <Typography color="primary.main" variant="body2">
                    {territory.label}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </>
    );
  };

  const cardContent = (
    <CardContent>
      <Grid container spacing={1}>
        <Grid size={12}>
          <Grid ref={usaTotalsElement}></Grid>
        </Grid>
        <Grid size={12}>
          <Grid>
            <Typography color="primary.main" variant="body1">
              {eventType}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );

  React.useEffect(() => {
    setChartId(`usa-map-${Date.now()}`);
    setLegendRangeMinValue(0);
    setLegendRangeMaxValue(10000);
  }, []);

  return (
    <Root $isCountyView={false} dontShowCountyData={true}>
      <Typography color="primary.main" variant="h2" align="center">
        <Grid container spacing={2}>
          <Grid
            size={{
              lg: 9,
              md: 12,
            }}
          >
            <Box
              sx={{
                display: "block",
              }}
            >
              {
                // @ts-ignore
                <ReactFC
                  id={chartId}
                  key={`${chartId}-${mapType}`}
                  {...mapChartProps}
                />
              }
            </Box>
          </Grid>
          <Grid
            size={{
              lg: 3,
              md: 12,
            }}
          >
            <Paper elevation={3} id="map-data-info">
              <Card className={classes.cardDisplay}>
                <CardHeader
                  id="USAMap-summaryDepositsTextBox"
                  ref={stateElement}
                  sx={{
                    position: "relative",
                    color: "white",
                    background: (theme) => theme.palette.primary.main,
                    fontSize: "1.25rem",
                    padding: ".5rem",
                  }}
                ></CardHeader>
                {cardContent}
              </Card>
            </Paper>
            <br />
            {
              <>
                {" "}
                <Grid>
                  <Typography
                    color="primary.main"
                    className={classes.statesHeading}
                  >
                    States & Territories not depicted
                  </Typography>
                </Grid>
                {showStatesAndTerritories(
                  legendRangeMinValue,
                  legendRangeMaxValue,
                )}
              </>
            }
          </Grid>
        </Grid>
      </Typography>
    </Root>
  );
}
